<template>
  <v-container class="alternative-container">
    <v-row no-gutters>
      <v-col cols="12" md="1">
        <router-link
          :to="{ name: 'RemainderOfEstateDistribution' }"
          class="back-arrow"
          ><v-icon large color="mid-grey" class="mt-1">{{
            icons.mdiArrowLeft
          }}</v-icon></router-link
        >
      </v-col>
      <v-col cols="12" md="3" class="form-blurb">
        <h2>Remainder of Estate</h2>
        <p>
          If one of the people you nominated to get (a portion of) the remainder
          of your estate predeceases you, how should that portion be
          distributed?
        </p>
      </v-col>
      <v-col cols="12" md="7" offset-md="1">
        <v-form @submit.prevent="submit">
          <h2 class="title-descr">
            Should one of the people who you nominated in the previous step
            predecease you, who would you like their portion to go to?
          </h2>
          <RadioSelect
            v-if="whitelabel.omDesign"
            v-model="alternativeDistribution"
            :items="alternativeDistributionItems"
            :color="'white'"
            :group="'distribution'"
            :flexColumn="true"
          />
          <v-radio-group v-else v-model="alternativeDistribution">
            <v-radio
              v-if="showTestatorsChildren"
              label="to my children, in equal shares"
              value="TESTATORS_CHILDREN"
              color="primary"
            />
            <v-radio
              v-if="showTestatorsChildrenOfPredeceased"
              label="to the children of the predeceased"
              value="CHILDREN_OF_PREDECEASED"
              color="primary"
            />
            <v-radio
              v-if="showDivideRemainingHeirs"
              label="it should be divided proportionately between the remaining heirs"
              value="DIVIDED_REMAINING_HEIRS"
              color="primary"
            />
            <v-radio
              :label="specificPercentagesLabel"
              value="SPECIFIC_PERCENTAGES"
              color="primary"
            />
          </v-radio-group>

          <GradientButton
            v-if="alternativeDistribution"
            :title="'Next'"
            :small="true"
            :callout="'right'"
            :loading="buttonLoading"
            type="submit"
          />
        </v-form>
      </v-col>
    </v-row>

    <ErrorSnackbar v-model="showError" />
  </v-container>
</template>

<script>
  import { TESTATOR_QUERY } from '../../constants/graphql/testators';
  import { UPDATE_REMAINDER_OF_ESTATE_MUTATION } from '../../constants/graphql/remainder-of-estates';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';

  export default {
    name: 'RemainderOfEstateAlternative',
    components: {
      ErrorSnackbar,
    },
    mixins: [formHelperMixin],
    props: {
      testator: {
        type: Object,
        required: true,
      },
    },
    data() {
      let alternativeDistribution = this.testator.remainderOfEstate
        .alternativeDistribution
        ? this.testator.remainderOfEstate.alternativeDistribution
        : null;

      if (
        alternativeDistribution === 'SPECIFIC_PEOPLE' ||
        alternativeDistribution === 'UNBORN_CHILDREN' ||
        alternativeDistribution === 'NOT_READY_FOR_DECISION'
      ) {
        // We're ignoring legacy options to force the user to go through the remainder of estate journey again to select correct options.
        alternativeDistribution = null;
      }
      return {
        alternativeDistribution: alternativeDistribution,
      };
    },
    computed: {
      showDivideRemainingHeirs() {
        if (
          this.testator.remainderOfEstate.distribution == 'SPOUSE' ||
          (this.testator.remainderOfEstate.distribution == 'CHILDREN' &&
            this.testator.numberOfChildren <= 1)
        ) {
          return false;
        } else {
          return true;
        }
      },
      showTestatorsChildren() {
        return (
          this.testator.remainderOfEstate.distribution == 'SPOUSE' &&
          this.testator.hasChildren
        );
      },
      showTestatorsChildrenOfPredeceased() {
        return this.testator.remainderOfEstate.distribution != 'SPOUSE';
      },
      showUnbornChildren() {
        return !this.testator.hasChildren;
      },
      specificPercentagesLabel() {
        if (this.testator.willCountry == 'GB') {
          return 'I want to set specific percentages to be distributed to specific people and/or charities';
        } else {
          return 'I want to set specific percentages to be distributed to specific people, charities and/or trusts';
        }
      },
      alternativeDistributionItems() {
        const items = [];

        if (this.showTestatorsChildren) {
          items.push({
            value: 'TESTATORS_CHILDREN',
            text: 'to my children, in equal shares',
          });
        }

        if (this.showTestatorsChildrenOfPredeceased) {
          items.push({
            value: 'CHILDREN_OF_PREDECEASED',
            text: 'to the children of the predeceased',
          });
        }

        if (this.showDivideRemainingHeirs) {
          items.push({
            value: 'DIVIDED_REMAINING_HEIRS',
            text: 'it should be divided proportionately between the remaining heirs',
          });
        }

        items.push({
          value: 'SPECIFIC_PERCENTAGES',
          text: this.specificPercentagesLabel,
        });

        return items;
      },
    },
    created() {
      if (
        !this.testator.remainderOfEstate ||
        !this.testator.remainderOfEstate.distribution
      )
        this.$router.push('/remainder-of-estate/distribution');
    },
    methods: {
      submit() {
        this.buttonLoading = true;
        this.updateRemainderOfEstate();
      },
      updateRemainderOfEstate() {
        const remainderOfEstateInputDetails = {
          alternativeDistribution: this.alternativeDistribution,
        };
        const id = this.testator.remainderOfEstate.id;
        this.$apollo
          .mutate({
            mutation: UPDATE_REMAINDER_OF_ESTATE_MUTATION,
            variables: {
              id,
              remainderOfEstateInputDetails,
            },
            update: (cache, { data: { updateRemainderOfEstate } }) => {
              this.updateCache(cache, updateRemainderOfEstate);
            },
          })
          .then(_response => {
            this.redirectToNext();
            this.buttonLoading = false;
          })
          .catch(error => {
            this.displayError();
            throw error;
          });
      },
      updateCache(cache, remainderOfEstate) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testator.id },
        };
        const data = cache.readQuery(query);

        data.testator.remainderOfEstate = remainderOfEstate;

        cache.writeQuery({ ...query, data: data });
      },
      redirectToNext() {
        if (this.alternativeDistribution == 'SPECIFIC_PERCENTAGES') {
          this.$router.push('alternative-distribution-specific-percentages');
        } else if (this.alternativeDistribution == 'SPECIFIC_PEOPLE') {
          this.$router.push({
            name: 'SpecificDonationsRemainderOfEstateOverview',
          });
        } else {
          this.$router.push({ name: 'FamilyObliteration' });
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .alternative-container {
    margin-bottom: 48px;

    .title-descr {
      line-height: 28px;
      font-weight: 700;
    }
  }
</style>
